import { ShieldCheck, BadgeCheck, Shield, HeartPulse, FileCheck, Trophy, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { COACHES } from "@/constants/site";
import BookingDialog from "@/components/BookingDialog";

const VALUES = [
  { icon: Trophy, title: "Excellence", text: "Pro-style training and curriculum, age-appropriate progression." },
  { icon: ShieldCheck, title: "Safety First", text: "DBS-checked, safeguarding-trained, first-aid certified coaches." },
  { icon: Sparkles, title: "Detail", text: "Each session is tailored towards each player." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/8941569/pexels-photo-8941569.jpeg" className="w-full h-full object-cover opacity-25" alt="Coach" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3A]/85 via-[#1E3A8A]/80 to-[#1E3A8A]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="eyebrow text-[#FBBF24]">About us</div>
          <h1 className="font-display heading-uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-3 max-w-3xl text-white">
            Striving for <span className="text-[#FBBF24]">development.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-slate-200 text-lg leading-relaxed">
            Advanced Coaching was founded in 2025 to bring academy-style football experiences to every child — regardless of starting level. We believe every child deserves expert coaching to boost their development.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="border border-slate-200 rounded-2xl p-8 bg-slate-50 hover-lift">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A8A] text-white grid place-items-center mb-5">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-display uppercase text-2xl">{v.title}</h3>
                <p className="text-slate-500 mt-3">{v.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="eyebrow text-[#1E3A8A]">Credentials</div>
          <h2 className="font-display heading-uppercase text-4xl sm:text-5xl mt-3 leading-none">Trust, built in.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { icon: ShieldCheck, label: "FA Qualified" },
              { icon: BadgeCheck, label: "DBS Checked" },
              { icon: Shield, label: "Safeguarding" },
              { icon: HeartPulse, label: "First Aid" },
              { icon: FileCheck, label: "Fully Insured" },
            ].map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.label} data-testid={`credential-${b.label.replace(/\s+/g, "-").toLowerCase()}`} className="flex flex-col items-center text-center p-6 rounded-2xl border border-slate-200 bg-white">
                  <Icon className="w-8 h-8 text-[#1E3A8A]" />
                  <div className="mt-3 text-xs font-semibold uppercase tracking-widest text-slate-700">{b.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="eyebrow text-[#1E3A8A]">Coaching team</div>
        <h2 className="font-display heading-uppercase text-4xl sm:text-5xl mt-3 leading-none">The people on the pitch.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {COACHES.map((c) => (
            <Link
              to={`/private-sessions?coach=${c.slug}#availability`}
              key={c.name}
              data-testid={`about-coach-link-${c.slug}`}
              className="group block overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 hover-lift focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/40"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <div className="eyebrow text-[#1E3A8A]">{c.role}</div>
                <h3 className="font-display uppercase text-2xl mt-1 group-hover:text-[#1E3A8A] transition-colors">{c.name}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{c.bio}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#1E3A8A]">
                  See availability <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16 rounded-3xl border border-slate-200 bg-slate-50 p-10 text-center">
          <h3 className="font-display uppercase text-3xl">Want to hit peak performance?</h3>
          <p className="text-slate-500 mt-2">Book your child's first session today.</p>
          <div className="mt-6 flex justify-center">
            <BookingDialog trigger={<button data-testid="about-cta-btn" className="btn-gold">Book a Session</button>} />
          </div>
        </div>
      </section>
    </div>
  );
}
